<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">{{ directT('dashboard.salesByLocations') }}</h5>
      </div>
      <div class="trezo-card-subtitle">
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block transition-all text-[26px] text-gray-500 dark:text-gray-400 leading-none hover:text-primary-500': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <i class="ri-more-fill"></i>
          </button>
          <ul
            class="trezo-card-dropdown-menu transition-all bg-white shadow-3xl rounded-md top-full py-[15px] absolute ltr:right-0 rtl:left-0 w-[195px] z-[10000] dark:bg-dark dark:shadow-none"
          >
            <li>
              <button
                type="button"
                @click="selectPeriod('day')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisDay') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('week')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisWeek') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('month')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisMonth') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('year')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisYear') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="trezo-card-content">
      <!-- Loading state -->
      <div v-if="isLoading" class="flex items-center justify-center min-h-[160px]">
        <div class="animate-spin inline-block w-8 h-8 border-[3px] border-current border-t-transparent text-primary-500 rounded-full" role="status" aria-label="loading">
          <span class="sr-only">{{ directT('common.loading') || 'Loading...' }}</span>
        </div>
      </div>

      <!-- No data state -->
      <div v-else-if="countries.length === 0" class="flex items-center justify-center min-h-[160px]">
        <div class="text-center text-gray-400 dark:text-gray-500">
          {{ directT('common.noData') || 'No data available' }}
        </div>
      </div>

      <!-- Data display -->
      <template v-else>
        <div class="flex items-center justify-center min-h-[160px]">
          <div class="text-center">
            <img src="@/assets/images/map.svg" alt="map" />
          </div>
        </div>
        <ul class="mt-[20px]">
          <li
            class="flex items-center mb-[15px] last:mb-0"
            v-for="(country, index) in countries"
            :key="index"
          >
            <div class="shrink-0 ltr:mr-[15px] rtl:ml-[15px]">
              <img
                :src="getFlagImagePath(country.image)"
                class="inline-block w-[24px] h-[24px] rounded-full object-cover"
                :alt="country.name"
              />
            </div>
            <div class="grow">
              <div class="flex justify-between">
                <span class="block font-medium mb-[2px]">
                  {{ country.name }}
                </span>
                <span class="text-sm text-gray-500">
                  {{ formatCurrency(country.amount) }}
                </span>
              </div>
              <div class="leading-none flex items-center">
                <div
                  class="flex w-full h-[4px] overflow-hidden rounded-md bg-primary-50 dark:bg-[#172036]"
                >
                  <div
                    class="flex flex-col justify-center overflow-hidden bg-primary-500 rounded-md"
                    :style="{ width: country.progress + '%' }"
                  ></div>
                </div>
                <span class="block ltr:ml-[15px] rtl:mr-[15px]">
                  {{ country.progress }}%
                </span>
              </div>
            </div>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { useI18n } from "vue-i18n";
import { getSalesByLocations, LocationSales } from "@/services/orderService";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Import flag images
import usaFlag from '@/assets/images/flags/usa.svg';
import ukFlag from '@/assets/images/flags/uk.svg';
import canadaFlag from '@/assets/images/flags/canada.svg';
import germanyFlag from '@/assets/images/flags/germany.svg';
import spainFlag from '@/assets/images/flags/spain.svg';
import portugalFlag from '@/assets/images/flags/portugal.svg';
import franceFlag from '@/assets/images/flags/france.svg';
import israelFlag from '@/assets/images/flags/israel.svg';
import australiaFlag from '@/assets/images/flags/australia.svg';
import brazilFlag from '@/assets/images/flags/brazil.svg';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

export default defineComponent({
  name: "SalesByLocations",
  setup() {
    const { locale } = useI18n();
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const isLoading = ref(false);
    const countries = ref<LocationSales[]>([]);
    const timeRange = ref('week'); // Default to 'week'

    // Map of flag images
    const flagImages: Record<string, string> = {
      'usa.svg': usaFlag,
      'uk.svg': ukFlag,
      'canada.svg': canadaFlag,
      'germany.svg': germanyFlag,
      'spain.svg': spainFlag,
      'portugal.svg': portugalFlag,
      'france.svg': franceFlag,
      'israel.svg': israelFlag,
      'australia.svg': australiaFlag,
      'brazil.svg': brazilFlag,
    };

    // Helper function to get the correct flag image path
    const getFlagImagePath = (image: string | undefined) => {
      if (!image) return israelFlag; // Default flag
      
      // If it's a full URL (for sample data)
      if (image.startsWith('http') || image.startsWith('blob')) {
        return image;
      }
      
      // Return the imported flag image or default to Israel
      return flagImages[image] || israelFlag;
    };

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };

    // Format currency with proper locale
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat(locale.value, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    };

    // Function to fetch sales by locations
    const fetchSalesByLocations = async (period = 'week') => {
      console.log(`[SalesByLocations] Fetching sales by locations for period: ${period}`);
      isLoading.value = true;
      try {
        timeRange.value = period;
        const { locations, error } = await getSalesByLocations(null, period);
        
        if (error) {
          console.error('[SalesByLocations] Error fetching sales by locations:', error);
          return;
        }

        console.log(`[SalesByLocations] Received ${locations.length} location records`);
        countries.value = locations;
      } catch (err) {
        console.error('[SalesByLocations] Exception in fetchSalesByLocations:', err);
      } finally {
        isLoading.value = false;
        console.log('[SalesByLocations] Loading finished');
      }
    };

    // Initial fetch on component mount
    onMounted(() => {
      console.log('[SalesByLocations] Component mounted, initializing data fetch');
      fetchSalesByLocations('week');
    });

    // Select time period
    const selectPeriod = (period: string) => {
      console.log(`[SalesByLocations] Period selected: ${period}`);
      fetchSalesByLocations(period);
      toggleActive();
    };

    return {
      isActive,
      toggleActive,
      countries,
      directT,
      isLoading,
      selectPeriod,
      formatCurrency,
      getFlagImagePath
    };
  },
});
</script>
